import React, { useState, useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import { BASEURI, getAuthToken, handleAuthTokenError } from '../utils';
import { GridContainer } from './AdminStationGrid';
import { useIntl } from 'react-intl';
import Spinner from '../general/Spinner';
import { subMonths, subYears, format } from 'date-fns';

export default function StationsPawStatusChart() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState('3m'); // Default to 3 months
    const chartRef = useRef(null);
    const intl = useIntl();

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            setLoading(true);
            try {
                const token = await getAuthToken();
                
                const endDate = new Date();
                let startDate;
                
                if (range === '1m') {
                    startDate = subMonths(endDate, 1);
                } else if (range === '3m') {
                    startDate = subMonths(endDate, 3);
                } else if (range === '6m') {
                    startDate = subMonths(endDate, 6);
                } else if (range === '1y') {
                    startDate = subYears(endDate, 1);
                } else if (range === '2y') {
                    startDate = subYears(endDate, 2);
                } else if (range === '3y') {
                    startDate = subYears(endDate, 3);
                } else {
                    startDate = subMonths(endDate, 3);
                }
                
                const startStr = format(startDate, 'yyyy-MM-dd');
                const endStr = format(endDate, 'yyyy-MM-dd');

                const response = await fetch(`${BASEURI}/api/dashboard/stations-paw-status-snapshots/?start=${startStr}&end=${endStr}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (response.status === 401) {
                    handleAuthTokenError();
                    return;
                }

                if (response.ok) {
                    const result = await response.json();
                    console.log('the result stations status', result)
                    if (isMounted) {
                        setData(result);
                        setLoading(false);
                    }
                } else {
                    if (isMounted) setLoading(false);
                }
            } catch (error) {
                console.error('Error fetching stations paw status:', error);
                if (isMounted) setLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [range]);

    useEffect(() => {
        if (!loading && data && chartRef.current) {
            // Sort data by date
            const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));

            const categories = sortedData.map(item => item.date);
            const adequateData = sortedData.map(item => item.paw_counts.adequate || 0);
            const excessiveData = sortedData.map(item => item.paw_counts.excessive || 0);
            const stressStartData = sortedData.map(item => item.paw_counts.stress_start || 0);
            const stressSevereData = sortedData.map(item => item.paw_counts.stress_severe || 0);

            Highcharts.chart(chartRef.current, {
                chart: {
                    type: 'column',
                    style: {
                        fontFamily: 'inherit'
                    }
                },
                title: {
                    text: intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.title', defaultMessage: 'Plant Available Water (PAW) Status by Station' }),
                    align: 'left'
                },
                xAxis: {
                    categories: categories,
                    crosshair: true,
                    title: {
                        text: intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.date', defaultMessage: 'Date' })
                    }
                },
                yAxis: {
                    min: 0,
                    title: {
                        text: intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.yAxisTitle', defaultMessage: 'Percentage of Sensors' })
                    }
                },
                tooltip: {
                    pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y}</b> ({point.percentage:.1f}%)<br/>',
                    shared: true
                },
                plotOptions: {
                    column: {
                        stacking: 'percent',
                        borderWidth: 0
                    }
                },
                series: [
                    {
                        id: "Exceso de agua",
                        name: intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.excessive', defaultMessage: 'Excess of water' }),
                        data: excessiveData,
                        color: '#00BFFF'
                    },
                    {
                        id: "Adecuado",
                        name: intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.adequate', defaultMessage: 'Optimal' }),
                        data: adequateData,
                        color: '#5DAE5D'
                    },
                    {
                        id: "Inicia stress",
                        name: intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.stressStart', defaultMessage: 'Start stress' }),
                        data: stressStartData,
                        color: '#EFEF66'
                    },
                    {
                        id: "Stress severo",
                        name: intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.stressSevere', defaultMessage: 'Severe stress' }),
                        data: stressSevereData,
                        color: '#EF6666'
                    }
                ],
                credits: {
                    enabled: false
                }
            });
        }
    }, [loading, data, intl.locale]);

    return (
        <GridContainer>
            <div className='w-full mt-3 mdl-shadow--2dp graph-grid'>
                <div className="w-full bg-white p-4" style={{ minHeight: '400px' }}>
                    <div className="flex justify-end space-x-2 rtl:space-x-reverse mb-4">
                        <button 
                            className={`px-3 py-1 text-sm rounded border ${range === '1m' ? 'border-green-500 text-green-600 bg-green-50' : 'border-transparent bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            onClick={() => setRange('1m')}
                            title={intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.range.1m.title', defaultMessage: '1 Month' })}
                        >
                            {intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.range.1m.abbr', defaultMessage: '1m' })}
                        </button>
                        <button 
                            className={`px-3 py-1 text-sm rounded border ${range === '3m' ? 'border-green-500 text-green-600 bg-green-50' : 'border-transparent bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            onClick={() => setRange('3m')}
                            title={intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.range.3m.title', defaultMessage: '3 Months' })}
                        >
                            {intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.range.3m.abbr', defaultMessage: '3m' })}
                        </button>
                        <button 
                            className={`px-3 py-1 text-sm rounded border ${range === '6m' ? 'border-green-500 text-green-600 bg-green-50' : 'border-transparent bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            onClick={() => setRange('6m')}
                            title={intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.range.6m.title', defaultMessage: '6 Months' })}
                        >
                            {intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.range.6m.abbr', defaultMessage: '6m' })}
                        </button>
                        <button 
                            className={`px-3 py-1 text-sm rounded border ${range === '1y' ? 'border-green-500 text-green-600 bg-green-50' : 'border-transparent bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            onClick={() => setRange('1y')}
                            title={intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.range.1y.title', defaultMessage: '1 Year' })}
                        >
                            {intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.range.1y.abbr', defaultMessage: '1y' })}
                        </button>
                        <button 
                            className={`px-3 py-1 text-sm rounded border ${range === '2y' ? 'border-green-500 text-green-600 bg-green-50' : 'border-transparent bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            onClick={() => setRange('2y')}
                            title={intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.range.2y.title', defaultMessage: '2 Years' })}
                        >
                            {intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.range.2y.abbr', defaultMessage: '2y' })}
                        </button>
                        <button 
                            className={`px-3 py-1 text-sm rounded border ${range === '3y' ? 'border-green-500 text-green-600 bg-green-50' : 'border-transparent bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            onClick={() => setRange('3y')}
                            title={intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.range.3y.title', defaultMessage: '3 Years' })}
                        >
                            {intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.range.3y.abbr', defaultMessage: '3y' })}
                        </button>
                    </div>
                    <div className="relative w-full" style={{ height: '350px' }}>
                        {loading ? (
                            <div className="flex items-center justify-center h-full w-full absolute top-0 left-0">
                                <Spinner color={'gray'} show={true} backDrop={false} size={30}/>
                            </div>
                        ) : (!data || data.length === 0) ? (
                            <div className="flex items-center justify-center h-full w-full absolute top-0 left-0 p-8 text-center text-gray-500">
                                {intl.formatMessage({ id: 'app.admin.stationsPawStatusChart.noData', defaultMessage: 'Plant Available Water (PAW) status by station is not available.' })}
                            </div>
                        ) : (
                            <div ref={chartRef} style={{ width: '100%', height: '100%' }} />
                        )}
                    </div>
                </div>
            </div>
        </GridContainer>
    );
}
